import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatDuration,
  formatFileSize,
} from "../../../../utils/exercise-video-preview";
import SvgIcon from "../../../SvgIcon";
import useYouTubeVideoMeta from "./useYouTubeVideoMeta";
import { ExternalIcon, PauseIcon, PlayIcon } from "./VideoPreviewIcons";
import VideoPreviewMedia from "./VideoPreviewMedia";

const ICON_STYLE = { width: 16, height: 16 };
const ADD_ICON_STYLE = { width: 10, height: 10 };

/** Object URL for a picked `File`, revoked when the file changes or the preview unmounts. */
function useObjectUrl(file) {
  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    if (!file) {
      setObjectUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return objectUrl;
}

/**
 * "Exercise video" card at the top of the Exercise details panel.
 *
 * `source` comes from `resolveVideoSource` (draft-driven, so it follows URL edits and
 * uploads live). YouTube duration and availability come from `useYouTubeVideoMeta`; an
 * uploaded video reports its own duration once its metadata loads. The parent remounts
 * this component (`key`) when the video changes, which resets playback.
 *
 * @param {{
 *   source: ReturnType<typeof import("../../../../utils/exercise-video-preview").resolveVideoSource>,
 *   pickedFile: File|null,
 *   enabled: boolean,
 *   onChangeVideo: () => void,
 * }} props
 */
function ExerciseVideoPreview({ source, pickedFile, enabled, onChangeVideo }) {
  const { t } = useTranslation("coaching-portal");
  const [started, setStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [player, setPlayer] = useState(null);
  const [playerFailed, setPlayerFailed] = useState(false);
  const [uploadDuration, setUploadDuration] = useState(null);
  const youtubeMeta = useYouTubeVideoMeta(
    source.kind === "youtube" ? source.videoId : null,
    enabled
  );
  const pickedFileUrl = useObjectUrl(pickedFile);

  const isNone = source.kind === "none";
  const isUpload = source.kind === "upload";
  const isUnavailable =
    source.kind === "youtube" && youtubeMeta.status === "unavailable";
  const uploadSrc = isUpload ? pickedFileUrl || source.playUrl : null;
  const canPlay = Boolean(isUpload ? uploadSrc : source.playUrl);
  const sourceLabel = isUpload
    ? t("addExerciseModal.videoPreview.uploaded")
    : source.badge;
  const fileSize = pickedFile ? formatFileSize(pickedFile.size) : null;
  const isControllable =
    (isUpload || source.kind === "youtube") && !playerFailed;
  const showPlayButton =
    !isUnavailable && canPlay && (!started || isControllable);
  const playButtonLoading = started && !player;

  const togglePlay = () => {
    if (!started) {
      setStarted(true);
    } else if (isPlaying) {
      player?.pause();
    } else {
      player?.play();
    }
  };

  const renderSourceLine = () => {
    if (isNone) {
      return (
        <>
          <strong>{t("addExerciseModal.videoPreview.noVideoLead")}</strong>{" "}
          {t("addExerciseModal.videoPreview.noVideoRest")}
        </>
      );
    }
    return (
      <>
        <strong>
          {isUpload
            ? t("addExerciseModal.videoPreview.uploadedFile")
            : sourceLabel}
        </strong>
        {" · "}
        {source.displayName}
        {fileSize ? ` · ${fileSize}` : null}
      </>
    );
  };

  const renderChip = () => {
    let chipKey = null;
    if (isNone) {
      chipKey = "addExerciseModal.videoPreview.videoMissing";
    } else if (isUnavailable) {
      chipKey = "addExerciseModal.videoPreview.privateOrRemoved";
    }
    if (!chipKey) {
      return null;
    }
    return (
      <div className="Add-Exercise-videoPreview-chips">
        <span className="chip warning Add-Exercise-metadataDetails-chip">
          {t(chipKey)}
        </span>
      </div>
    );
  };

  const renderActions = () => {
    if (isNone) {
      return (
        <button
          type="button"
          data-button-type="secondary"
          onClick={onChangeVideo}
        >
          <SvgIcon
            src="/img/svg/white-plus.svg"
            useCurrentColor
            style={ADD_ICON_STYLE}
          />
          {t("addExerciseModal.videoPreview.addVideo")}
        </button>
      );
    }
    let openLabel = t("addExerciseModal.videoPreview.openLink");
    if (isUnavailable) {
      openLabel = t("addExerciseModal.videoPreview.checkOnYouTube");
    } else if (source.kind === "youtube") {
      openLabel = t("addExerciseModal.videoPreview.openOnYouTube");
    }
    return (
      <>
        {showPlayButton ? (
          <button
            type="button"
            data-button-type="secondary"
            disabled={playButtonLoading}
            onClick={togglePlay}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
            {isPlaying
              ? t("addExerciseModal.videoPreview.pause")
              : t("addExerciseModal.videoPreview.play")}
          </button>
        ) : null}
        {source.openUrl ? (
          <a
            className="Add-Exercise-videoPreview-link"
            href={source.openUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalIcon />
            {openLabel}
          </a>
        ) : null}
        <button
          type="button"
          className={
            isUnavailable ? undefined : "Add-Exercise-videoPreview-link"
          }
          data-button-type={isUnavailable ? "secondary" : undefined}
          onClick={onChangeVideo}
        >
          <SvgIcon
            src="/img/new-icons/edit-pencil.svg"
            useCurrentColor
            style={ICON_STYLE}
          />
          {isUnavailable
            ? t("addExerciseModal.videoPreview.fixLink")
            : t("addExerciseModal.videoPreview.changeVideo")}
        </button>
      </>
    );
  };

  return (
    <section
      className="Add-Exercise-videoPreview"
      data-testid="exercise-video-preview"
    >
      <VideoPreviewMedia
        source={source}
        badgeLabel={sourceLabel}
        duration={formatDuration(
          isUpload ? uploadDuration : youtubeMeta.duration
        )}
        isUnavailable={isUnavailable}
        playing={started}
        uploadSrc={uploadSrc}
        onPlay={togglePlay}
        onUploadMetadata={setUploadDuration}
        onPlayerChange={setPlayer}
        onPlayingChange={setIsPlaying}
        onPlayerFailed={() => setPlayerFailed(true)}
      />
      <div className="Add-Exercise-videoPreview-body">
        <span className="Add-Exercise-videoPreview-eyebrow">
          {t("addExerciseModal.videoPreview.title")}
        </span>
        <p className="Add-Exercise-videoPreview-source">{renderSourceLine()}</p>
        {renderChip()}
        <div className="Add-Exercise-videoPreview-actions">
          {renderActions()}
        </div>
      </div>
    </section>
  );
}

ExerciseVideoPreview.propTypes = {
  source: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    badge: PropTypes.string,
    displayName: PropTypes.string,
    videoId: PropTypes.string,
    openUrl: PropTypes.string,
    playUrl: PropTypes.string,
  }).isRequired,
  pickedFile: PropTypes.object,
  enabled: PropTypes.bool,
  onChangeVideo: PropTypes.func.isRequired,
};

export default ExerciseVideoPreview;
